import { useEffect, useState, useCallback } from "react";
import { DataTable, ColumnDef, FilterOption } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Download } from "lucide-react";
// import { api, ApiClientError } from "@/lib/api"; // Uncomment when backend is ready

// Define the Registry type
interface Registry {
  id: string;
  registry_name: string;
  ncii_instances: number;
  type: string;
  region: string;
  active_urls: number;
  removed_urls: number;
  takedown_rate: number; // Percentage as decimal (e.g., 0.85 for 85%)
  avg_response_time: number; // In days
  contacts: string[]; // Array of email addresses
}

const PAGE_SIZE = 50;

export function RegistryList() {
  const { logout } = useAuth();
  const [registries, setRegistries] = useState<Registry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [_totalCount, setTotalCount] = useState<number>(0);

  const loadRegistries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // TODO: Replace with actual API call
      // const data = await api.registries.list(PAGE_SIZE, currentPage);
      // setRegistries(data);
      // setHasMore(data.length === PAGE_SIZE);
      // setTotalCount(data.totalCount); // Assuming API returns total count

      // Mock data for now
      const mockRegistries: Registry[] = [
        {
          id: "1",
          registry_name: "Verisign (.com, .net)",
          ncii_instances: 892,
          type: "gTLD",
          region: "Global",
          active_urls: 167,
          removed_urls: 725,
          takedown_rate: 0.81,
          avg_response_time: 3.8,
          contacts: ["abuse@verisign.com", "compliance@verisign.com"],
        },
        {
          id: "2",
          registry_name: "PIR (.org)",
          ncii_instances: 634,
          type: "gTLD",
          region: "Global",
          active_urls: 118,
          removed_urls: 516,
          takedown_rate: 0.81,
          avg_response_time: 3.5,
          contacts: ["abuse@pir.org", "legal@pir.org"],
        },
        {
          id: "3",
          registry_name: "Identity Digital (.info, .biz)",
          ncii_instances: 523,
          type: "gTLD",
          region: "Global",
          active_urls: 97,
          removed_urls: 426,
          takedown_rate: 0.81,
          avg_response_time: 3.2,
          contacts: ["abuse@identity.digital"],
        },
        {
          id: "4",
          registry_name: "Nominet (.uk)",
          ncii_instances: 412,
          type: "ccTLD",
          region: "Europe",
          active_urls: 76,
          removed_urls: 336,
          takedown_rate: 0.82,
          avg_response_time: 2.9,
          contacts: ["abuse@nominet.uk", "legal@nominet.uk"],
        },
        {
          id: "5",
          registry_name: "DENIC (.de)",
          ncii_instances: 467,
          type: "ccTLD",
          region: "Europe",
          active_urls: 87,
          removed_urls: 380,
          takedown_rate: 0.81,
          avg_response_time: 3.1,
          contacts: ["abuse@denic.de"],
        },
        {
          id: "6",
          registry_name: "EURid (.eu)",
          ncii_instances: 298,
          type: "ccTLD",
          region: "Europe",
          active_urls: 55,
          removed_urls: 243,
          takedown_rate: 0.82,
          avg_response_time: 2.8,
          contacts: ["abuse@eurid.eu", "legal@eurid.eu"],
        },
        {
          id: "7",
          registry_name: "CNNIC (.cn)",
          ncii_instances: 578,
          type: "ccTLD",
          region: "Asia",
          active_urls: 107,
          removed_urls: 471,
          takedown_rate: 0.81,
          avg_response_time: 4.2,
          contacts: ["abuse@cnnic.cn"],
        },
        {
          id: "8",
          registry_name: "auDA (.au)",
          ncii_instances: 345,
          type: "ccTLD",
          region: "Oceania",
          active_urls: 64,
          removed_urls: 281,
          takedown_rate: 0.81,
          avg_response_time: 3.3,
          contacts: ["abuse@auda.org.au", "compliance@auda.org.au"],
        },
        {
          id: "9",
          registry_name: "CIRA (.ca)",
          ncii_instances: 389,
          type: "ccTLD",
          region: "North America",
          active_urls: 72,
          removed_urls: 317,
          takedown_rate: 0.81,
          avg_response_time: 3.0,
          contacts: ["abuse@cira.ca"],
        },
        {
          id: "10",
          registry_name: "Registro.br (.br)",
          ncii_instances: 456,
          type: "ccTLD",
          region: "South America",
          active_urls: 85,
          removed_urls: 371,
          takedown_rate: 0.81,
          avg_response_time: 3.6,
          contacts: ["abuse@registro.br", "legal@registro.br"],
        },
      ];
      setRegistries(mockRegistries);
      setHasMore(false);
      setTotalCount(mockRegistries.length); // For mock data, total equals current data length
    } catch (err) {
      // if (err instanceof ApiClientError) {
      //   if (err.status === 401) {
      //     logout();
      //   } else {
      //     setError(err.detail || "Failed to load registries");
      //   }
      // } else {
      //   setError("Failed to load registries");
      // }
      setError("Failed to load registries");
    } finally {
      setLoading(false);
    }
  }, [currentPage, logout]);

  useEffect(() => {
    loadRegistries();
  }, [loadRegistries]);

  const handleNextPage = () => {
    if (!loading && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (!loading && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Define columns for the Registries table
  const columns: ColumnDef<Registry>[] = [
    {
      key: "registry_name",
      header: "Registry Name",
      render: (item) => (
        <div className="text-xs font-medium text-foreground">
          {item.registry_name}
        </div>
      ),
      sortValue: (item) => item.registry_name,
    },
    {
      key: "ncii_instances",
      header: "NCII Instances",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.ncii_instances}
        </div>
      ),
      sortValue: (item) => item.ncii_instances,
    },
    {
      key: "type",
      header: "Type",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.type}</div>
      ),
      sortValue: (item) => item.type,
    },
    {
      key: "region",
      header: "Region",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.region}</div>
      ),
      sortValue: (item) => item.region,
    },
    {
      key: "active_urls",
      header: "Active URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.active_urls}</div>
      ),
      sortValue: (item) => item.active_urls,
    },
    {
      key: "removed_urls",
      header: "Removed URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.removed_urls}</div>
      ),
      sortValue: (item) => item.removed_urls,
    },
    {
      key: "takedown_rate",
      header: "Takedown Rate",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {(item.takedown_rate * 100).toFixed(1)}%
        </div>
      ),
      sortValue: (item) => item.takedown_rate,
    },
    {
      key: "avg_response_time",
      header: "Avg Response Time",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.avg_response_time.toFixed(1)} days
        </div>
      ),
      sortValue: (item) => item.avg_response_time,
    },
    {
      key: "contacts",
      header: "Contacts",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.contacts.join(", ")}
        </div>
      ),
      sortValue: (item) => item.contacts.join(", "),
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      render: (item) => (
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-3"
          onClick={() => {
            // TODO: Navigate to registry details or open modal
            console.log("View registry:", item.id);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  // Define filters (for search functionality)
  const filters: FilterOption[] = [
    { label: "Type", placeholder: "All Types" },
    { label: "Region", placeholder: "All Regions" },
  ];

  // Handle new registry creation
  const handleNewRegistry = () => {
    // TODO: Implement new registry modal or navigation
    console.log("Add new registry");
  };

  // Handle export
  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export registries");
  };

  // Action buttons for the header
  const actionButtons = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        className="h-9 px-4"
      >
        <Download className="h-4 w-4 mr-2" />
        Export
      </Button>
      <Button
        size="sm"
        onClick={handleNewRegistry}
        className="h-9 px-4"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Registry
      </Button>
    </>
  );

  return (
    <DataTable
      title="Registries"
      subtitle="Track top-level domain registries"
      columns={columns}
      data={registries}
      loading={loading}
      error={error}
      filters={filters}
      searchPlaceholder="Search registries..."
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      hasNextPage={hasMore}
      hasPreviousPage={currentPage > 1}
      currentPage={currentPage}
      itemsPerPage={PAGE_SIZE}
      actionButtons={actionButtons}
      totalCount={undefined}
      entityName="registries"
    />
  );
}



